import React, { useState } from "react";
import {
  Database,
  Scale,
  BookOpen,
  ShieldCheck,
  Sparkles,
  FileText,
  ArrowRight,
  CheckCircle2,
  Cpu,
  Search,
  Building2,
  FileCode2,
  Layers,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

type CategoryTab = "altas_cortes" | "leyes_decretos" | "sentencias" | "rag_ia";

interface CategoryItem {
  name: string;
  desc: string;
  badge: string;
  icon: React.ElementType;
  tag: string;
}

interface CategoryConfig {
  title: string;
  subtitle: string;
  items: CategoryItem[];
}

const KnowledgeBaseSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CategoryTab>("altas_cortes");

  const categoryDetails: Record<CategoryTab, CategoryConfig> = {
    altas_cortes: {
      title: "Jurisprudencia de Altas Cortes Colombianas",
      subtitle: "Sentencias de unificación (SU), fallos de constitucionalidad (C), tutela (T) y precedentes vinculantes.",
      items: [
        {
          name: "Corte Constitucional",
          desc: "Sentencias C, T y SU, autos de seguimiento y gacetas constitucionales desde 1992 a la fecha.",
          badge: "650,000+ Fallos",
          icon: Scale,
          tag: "Unificación & Tutela",
        },
        {
          name: "Corte Suprema de Justicia",
          desc: "Salas de Casación Civil y Agraria, Penal, Laboral, Especial de Primera Instancia y Sala Plena.",
          badge: "520,000+ Decisiones",
          icon: Building2,
          tag: "Casación Ordinaria",
        },
        {
          name: "Consejo de Estado",
          desc: "Secciones Primera a Quinta, Sala de Consulta y Servicio Civil, acciones populares y de grupo.",
          badge: "410,000+ Providencias",
          icon: FileText,
          tag: "Contencioso Administrativo",
        },
      ],
    },
    leyes_decretos: {
      title: "Marco Normativo & Legislación Vigente",
      subtitle: "Compilación integral y concordada del ordenamiento jurídico colombiano actualizado.",
      items: [
        {
          name: "Constitución & Códigos",
          desc: "Constitución de 1991, CGP, CPACA, Código Penal, Civil, Comercio y Sustantivo del Trabajo.",
          badge: "Codificación Total",
          icon: BookOpen,
          tag: "Códigos Nacionales",
        },
        {
          name: "Leyes del Congreso",
          desc: "Leyes ordinarias, estatutarias, orgánicas y marcos reglamentarios expedidos por el legislativo.",
          badge: "120,000+ Leyes",
          icon: FileCode2,
          tag: "Leyes Vigentes",
        },
        {
          name: "Decretos Reglamentarios",
          desc: "Decretos Únicos Reglamentarios (DUR) por sector, decretos ley y directivas ministeriales oficiales.",
          badge: "150,000+ Decretos",
          icon: Layers,
          tag: "DUR Sectoriales",
        },
      ],
    },
    sentencias: {
      title: "Tribunales Seccionales & Precedentes",
      subtitle: "Jurisprudencia de tribunales superiores de distrito y tribunales administrativos del país.",
      items: [
        {
          name: "Tribunales de Distrito",
          desc: "Decisiones de segunda instancia en Bogotá, Medellín, Cali, Barranquilla, Bucaramanga y todo el país.",
          badge: "Segunda Instancia",
          icon: Scale,
          tag: "Tribunales Ordinarios",
        },
        {
          name: "Tribunales Administrativos",
          desc: "Controversias contractuales estatales, reparación directa, nulidad y restablecimiento del derecho.",
          badge: "Justicia Estatal",
          icon: Building2,
          tag: "Tribunales Contenciosos",
        },
        {
          name: "Líneas Jurisprudenciales",
          desc: "Extracción automática de Ratio Decidendi y Obiter Dicta para detectar cambios de criterio judicial.",
          badge: "Precedente Vivo",
          icon: Search,
          tag: "Ratio Decidendi",
        },
      ],
    },
    rag_ia: {
      title: "Arquitectura RAG & Inteligencia Artificial",
      subtitle: "Tecnología de recuperación semántica que garantiza respuestas sin alucinaciones.",
      items: [
        {
          name: "Vectorización Semántica",
          desc: "Fragmentación de providencias en embeddings legales de alta precisión para cotejos en milisegundos.",
          badge: "Embeddings Legales",
          icon: Cpu,
          tag: "Indexación Vectorial",
        },
        {
          name: "Citación de Soporte Real",
          desc: "Cada concepto, cálculo o minuta redactada incluye el artículo, auto o radicado oficial exacto.",
          badge: "Cero Alucinaciones",
          icon: ShieldCheck,
          tag: "Trazabilidad Total",
        },
        {
          name: "Sincronización Semanal",
          desc: "Actualización continua con las gacetas de relatoría oficial de las Altas Cortes y Diario Oficial.",
          badge: "Data al Día",
          icon: Database,
          tag: "Relatorías 2024-2026",
        },
      ],
    },
  };

  const currentTab = categoryDetails[activeTab];

  return (
    <section
      id="hero"
      className="pt-16 pb-16 lg:pt-20 lg:pb-20 bg-gradient-to-b from-white via-slate-50 to-slate-100/70 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-slate-900 dark:text-white relative overflow-hidden"
    >
      {/* Subtle modern ambient background glow */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[850px] h-[380px] bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-teal-500/10 rounded-full blur-[110px] pointer-events-none" />
      <div className="absolute top-24 right-10 w-72 h-72 bg-blue-400/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-72 h-72 bg-indigo-400/5 rounded-full blur-3xl pointer-events-none" />

      {/* Decorative Grid Pattern */}
      <div
        className="absolute inset-0 opacity-[0.025] dark:opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        {/* Hero Headline Area */}
        <div className="max-w-4xl mx-auto text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/60 border border-blue-200/80 dark:border-blue-900 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            <span>Sistema Operativo Legal • Base de +1.8M Archivos</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-5 leading-tight text-slate-900 dark:text-white">
            Soluciones Legales con{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 dark:from-blue-400 dark:to-indigo-300">
              IA
            </span>
          </h1>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg md:text-xl font-medium leading-relaxed max-w-2xl mx-auto mb-8">
            Asistencia profesional respaldada por una base de conocimiento viva de{" "}
            <strong className="text-slate-900 dark:text-white font-extrabold">
              1.8 millones de providencias y normas
            </strong>{" "}
            de las Altas Cortes colombianas.
          </p>

          {/* Action CTAs */}
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center mb-10">
            <Link to="/signup">
              <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full text-sm sm:text-base font-extrabold h-12 px-8 shadow-lg shadow-blue-600/25 hover:shadow-xl hover:scale-105 transition-all duration-300 uppercase tracking-wider flex items-center justify-center gap-2">
                <span>COMENZAR AHORA</span>
                <ArrowRight size={16} />
              </Button>
            </Link>
            <a href="#features">
              <Button
                variant="outline"
                className="w-full sm:w-auto bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-white rounded-full text-sm sm:text-base font-extrabold h-12 px-7 border border-slate-300 dark:border-slate-800 shadow-sm hover:bg-slate-50 transition-all duration-300"
              >
                Ver Soluciones de Despacho ↓
              </Button>
            </a>
          </div>

          {/* Metrics Card (Uniform with clean site style) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3.5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-xl shadow-slate-900/5 max-w-4xl mx-auto mb-10">
            <div className="p-2.5 border-r border-slate-100 dark:border-slate-800 last:border-0 text-center">
              <div className="text-2xl sm:text-3xl font-black text-blue-600 dark:text-blue-400">1.8M+</div>
              <div className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                Archivos Oficiales
              </div>
            </div>
            <div className="p-2.5 border-r border-slate-100 dark:border-slate-800 last:border-0 text-center">
              <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">3 Altas Cortes</div>
              <div className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                Constitucional, CSJ, CE
              </div>
            </div>
            <div className="p-2.5 border-r border-slate-100 dark:border-slate-800 last:border-0 text-center">
              <div className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400">30+ Años</div>
              <div className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                Histórico Normativo
              </div>
            </div>
            <div className="p-2.5 text-center">
              <div className="text-2xl sm:text-3xl font-black text-indigo-600 dark:text-indigo-400">100% RAG</div>
              <div className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                Citas Verificables
              </div>
            </div>
          </div>
        </div>

        {/* Streamlined Knowledge Base Browser */}
        <div className="max-w-5xl mx-auto">
          {/* Tabs Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {[
              { id: "altas_cortes", label: "Jurisprudencia Altas Cortes", icon: Scale },
              { id: "leyes_decretos", label: "Leyes y Decretos", icon: BookOpen },
              { id: "sentencias", label: "Sentencias & Tribunales", icon: FileText },
              { id: "rag_ia", label: "Tecnología RAG & IA", icon: Cpu },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as CategoryTab)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 border ${
                    isActive
                      ? "bg-slate-900 dark:bg-white text-white dark:text-slate-950 border-slate-900 dark:border-white shadow-md shadow-slate-900/10 scale-105"
                      : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200/80 dark:border-slate-800 hover:border-slate-300 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-blue-400 dark:text-blue-600" : "text-blue-600 dark:text-blue-400"}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Compact Tab Content Display Card */}
          <div className="p-6 sm:p-7 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-xl shadow-slate-900/5 relative overflow-hidden">
            <div className="mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
                  {currentTab.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium mt-0.5">
                  {currentTab.subtitle}
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/50 px-3 py-1 rounded-full border border-emerald-200/80 dark:border-emerald-800 w-fit shrink-0">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>Indexación Activa</span>
              </div>
            </div>

            {/* 3 Compact Court / Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {currentTab.items.map((item, idx) => {
                const ItemIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-5 rounded-2xl bg-slate-50/70 dark:bg-slate-950/50 border border-slate-200/70 dark:border-slate-800 hover:border-blue-300 dark:hover:border-slate-700 hover:shadow-md transition-all duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/70 text-blue-600 dark:text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                          <ItemIcon className="w-5 h-5" />
                        </div>
                        <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-blue-100/70 dark:bg-blue-950/70 text-blue-700 dark:text-blue-300 border border-blue-200/80 dark:border-blue-800">
                          {item.badge}
                        </span>
                      </div>

                      <h4 className="text-base font-extrabold text-slate-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {item.name}
                      </h4>
                      <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-200/60 dark:border-slate-800 flex items-center justify-between text-[11px] text-blue-600 dark:text-blue-400 font-semibold">
                      <span className="flex items-center gap-1 text-slate-700 dark:text-slate-300">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {item.tag}
                      </span>
                      <span className="group-hover:translate-x-0.5 transition-transform">
                        Indexado &rarr;
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Integrated 1-Line Trust Strip */}
            <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-around gap-4 text-xs font-semibold text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span>Cero Alucinaciones: Solo citas comprobables</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span>Precedentes actualizados semanalmente</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>Conexión directa con agentes de redacción y CRM</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default KnowledgeBaseSection;
